import { IWorkingHour } from "../../models/IWorkingHour";
import { IScheduleOverride } from "../models/IAvailability";

export const DAYS: { value: number; label: string; short: string }[] = [
  { value: 1, label: "Pazartesi", short: "Pzt" },
  { value: 2, label: "Salı",      short: "Sal" },
  { value: 3, label: "Çarşamba",  short: "Çrş" },
  { value: 4, label: "Perşembe",  short: "Prş" },
  { value: 5, label: "Cuma",      short: "Cum" },
  { value: 6, label: "Cumartesi", short: "Cmt" },
  { value: 7, label: "Pazar",     short: "Paz" },
];

export const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

export const toTimeSpan = (t: string) => (t.length === 5 ? t + ":00" : t);
export const toHHmm = (t: string) => t?.slice(0, 5) ?? "";

/** Kapanış açılıştan önceyse vardiya ertesi güne taşar (18:00–02:00). Backend de böyle yorumlar. */
export const crossesMidnight = (open: string, close: string) => toHHmm(close) < toHHmm(open);

/** ISO gün numarası: 1=Pazartesi … 7=Pazar */
export const isoDay = (d: Date) => (d.getDay() === 0 ? 7 : d.getDay());

/** Bu haftanın (bugünden itibaren) ilgili gününün tarihi. */
export function dateForDay(dayValue: number): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = (dayValue - isoDay(today) + 7) % 7;
  const d = new Date(today);
  d.setDate(today.getDate() + diff);
  return d;
}

/** API'den gelen tarih saat dilimi belirtmiyorsa UTC kabul edilir. */
export function parseUtc(s?: string | null): Date | null {
  if (!s) return null;
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasZone ? s : s + "Z");
  return isNaN(d.getTime()) ? null : d;
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "Bugün 18:00", "Yarın 09:00", "Cuma 09:00", "12 Eki 09:00" */
export function friendlyDateTime(d: Date): string {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const time = d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, now)) return `Bugün ${time}`;
  if (sameDay(d, tomorrow)) return `Yarın ${time}`;
  const days = (d.getTime() - now.getTime()) / 86400000;
  if (days > 0 && days < 6) return `${DAYS[isoDay(d) - 1].label} ${time}`;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() !== now.getFullYear() ? " " + d.getFullYear() : ""} ${time}`;
}

const isMidnight = (d: Date) => d.getHours() === 0 && d.getMinutes() === 0;
const shortDate = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;

/** İstisna aralığını okunur yazar; gece yarısından gece yarısına aralıkları "tüm gün" olarak gösterir. */
export function describeWindow(o: IScheduleOverride): { title: string; sub: string } {
  const start = parseUtc(o.startsAtUtc)!;
  const end = parseUtc(o.endsAtUtc);
  if (!end) return { title: `${friendlyDateTime(start)} itibarıyla`, sub: "Bitiş yok — elle kaldırılana kadar" };
  if (isMidnight(start) && isMidnight(end)) {
    const lastDay = new Date(end);
    lastDay.setDate(end.getDate() - 1);
    const dayCount = Math.round((end.getTime() - start.getTime()) / 86400000);
    return sameDay(start, lastDay)
      ? { title: `${shortDate(start)}, ${DAYS[isoDay(start) - 1].label}`, sub: "Tüm gün" }
      : { title: `${shortDate(start)} – ${shortDate(lastDay)}`, sub: `${dayCount} gün, tüm gün` };
  }
  return { title: `${friendlyDateTime(start)} → ${friendlyDateTime(end)}`, sub: durationText(end.getTime() - start.getTime()) };
}

export function durationText(ms: number): string {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} dakika`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h} saat ${m} dk` : `${h} saat`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d} gün ${rh} saat` : `${d} gün`;
}

/** Kartta gösterilecek: o tarihe denk gelen istisna (varsa en son eklenen). */
export function overrideOnDate(overrides: IScheduleOverride[], date: Date): IScheduleOverride | null {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayStart.getDate() + 1);
  const hits = overrides.filter((o) => {
    const s = parseUtc(o.startsAtUtc)!;
    const e = parseUtc(o.endsAtUtc);
    return s < dayEnd && (!e || e > dayStart);
  });
  hits.sort((a, b) => (parseUtc(b.createdAtUtc)?.getTime() ?? 0) - (parseUtc(a.createdAtUtc)?.getTime() ?? 0));
  return hits[0] ?? null;
}

export function hourForDay(hours: IWorkingHour[], day: number) {
  return hours.find((h) => h.dayOfWeek === day);
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: unknown } };
  if (e?.response?.status === 403) return "Bu işlem için yetkiniz yok.";
  const data = e?.response?.data;
  const first = Array.isArray(data) ? data[0] : null;
  if (first?.description) return first.description;
  const errors = (data as { errors?: Record<string, string[]> })?.errors;
  if (errors) {
    const msg = Object.values(errors).flat()[0];
    if (msg) return msg;
  }
  return fallback;
}
